import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import type {
	AppItems,
	AppItemType,
} from '../../../../common/ui/more-apps/apps-group/apps-group-item/types';

import { SEARCH_DEBOUNCE_MS } from './constants';
import { messages } from './messages';
import type { FilterAppsProps } from './types';

const styles = cssMap({
	searchIconContainer: {
		paddingInlineStart: token('space.100'),
	},
});

export const FilterApps = (props: FilterAppsProps): React.JSX.Element => {
	const { onChange, appItems } = props;
	const [searchInputValue, setSearchInputValue] = useState('');
	const intl = useIntl();

	const [debouncedSetSearchValue] = useDebouncedCallback((inputValue: string) => {
		const filteredAppItems: AppItems = {
			appItems: [],
			atlassianAppsItems: [],
			integrationsAppsItems: [],
			marketplaceAppsItems: [],
		};

		for (const [key, value] of Object.entries(appItems)) {
			filteredAppItems[key as AppItemType] = value.filter((item) =>
				item.title.toLowerCase().includes(inputValue.toLowerCase()),
			);
		}

		onChange(filteredAppItems);
	}, SEARCH_DEBOUNCE_MS);

	const onChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setSearchInputValue(value);
			debouncedSetSearchValue(value);
		},
		[debouncedSetSearchValue],
	);

	return (
		<TextField
			elemBeforeInput={
				<Box xcss={styles.searchIconContainer}>
					<SearchIcon color={token('color.icon.subtlest')} spacing="spacious" label="" />
				</Box>
			}
			value={searchInputValue}
			onChange={onChangeHandler}
			placeholder={intl.formatMessage(messages.searchAppsPlaceholder)}
		/>
	);
};
